import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UploadCloud, FileText, Check, X, Loader2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { Reveal, Kicker } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ASSETS } from "@/data/content";
import usePageMeta from "@/hooks/usePageMeta";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 5 * 1024 * 1024;

const Careers = () => {
  const { t } = useLang();
  usePageMeta({ title: `${t.nav.careers} | ${t.company.short}` });
  const f = t.careers.fields;
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    position: "",
    experience: "",
    cover_note: "",
  });

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const pickFile = (selected) => {
    if (!selected) return;
    const ext = selected.name.split(".").pop().toLowerCase();
    if (!["pdf", "doc", "docx"].includes(ext)) {
      toast.error(t.careers.error, { description: "PDF / DOC / DOCX only" });
      return;
    }
    if (selected.size > MAX_SIZE) {
      toast.error(t.careers.error, { description: "Max 5MB" });
      return;
    }
    setFile(selected);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) {
      toast.error(t.careers.error, { description: f.resume });
      return;
    }
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("resume", file);
      await axios.post(`${API}/careers`, fd);
      toast.success(t.careers.success);
      setForm({ full_name: "", email: "", phone: "", position: "", experience: "", cover_note: "" });
      setFile(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || t.careers.error;
      toast.error(t.careers.error, { description: typeof msg === "string" ? msg : "" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="careers-page" className="bg-[#F5F2EB]">
      {/* Hero band */}
      <section className="relative pt-40 pb-20 md:pt-48 md:pb-28 bg-[#141414] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={ASSETS.darZaidImg} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <Kicker className="text-[#C4A27A]">{t.careers.kicker}</Kicker>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-5 max-w-3xl">
            {t.careers.title}
          </h1>
          <p className="text-lg text-white/80 mt-6 max-w-xl">{t.careers.text}</p>
        </div>
      </section>

      <section className="py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Culture */}
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display text-3xl md:text-4xl text-[#1A1A1A] mb-8">
                {t.careers.cultureTitle}
              </h2>
            </Reveal>
            <ul className="space-y-5">
              {t.careers.culture.map((c, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <li className="flex items-start gap-4">
                    <span className="mt-1 w-6 h-6 rounded-full bg-[#6A1E27] text-white grid place-items-center shrink-0">
                      <Check className="w-3.5 h-3.5" />
                    </span>
                    <span className="text-base md:text-lg text-[#4A4A4A]">{c}</span>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>

          {/* Application form */}
          <div className="lg:col-span-7">
            <Reveal>
              <div className="bg-white border border-[#E5DFD3] p-8 md:p-12">
                <h3 className="font-display text-2xl md:text-3xl text-[#1A1A1A] mb-8">
                  {t.careers.formTitle}
                </h3>
                <form onSubmit={submit} data-testid="careers-form" className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <Field label={f.fullName}>
                      <Input data-testid="career-name" name="full_name" required value={form.full_name} onChange={onChange} />
                    </Field>
                    <Field label={f.email}>
                      <Input data-testid="career-email" type="email" name="email" required value={form.email} onChange={onChange} />
                    </Field>
                    <Field label={f.phone}>
                      <Input data-testid="career-phone" name="phone" required value={form.phone} onChange={onChange} />
                    </Field>
                    <Field label={f.position}>
                      <Input data-testid="career-position" name="position" required value={form.position} onChange={onChange} />
                    </Field>
                    <Field label={f.experience}>
                      <Input data-testid="career-experience" name="experience" required value={form.experience} onChange={onChange} placeholder="e.g. 3" />
                    </Field>
                  </div>

                  {/* CV upload */}
                  <div>
                    <Label className="text-sm text-[#4A4A4A] mb-2 block">{f.resume}</Label>
                    <div
                      data-testid="cv-dropzone"
                      onClick={() => fileRef.current?.click()}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        pickFile(e.dataTransfer.files?.[0]);
                      }}
                      className="border-2 border-dashed border-[#D8CFBE] hover:border-[#6A1E27] transition-colors cursor-pointer p-8 text-center"
                    >
                      {file ? (
                        <div className="flex items-center justify-center gap-3 text-[#1A1A1A]">
                          <FileText className="w-5 h-5 text-[#6A1E27]" />
                          <span className="text-sm">{file.name}</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setFile(null); }}
                            className="text-[#8A8478] hover:text-[#6A1E27]"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center gap-2 text-[#8A8478]">
                          <UploadCloud className="w-8 h-8" />
                          <span className="text-sm">{f.uploadHint}</span>
                        </div>
                      )}
                      <input
                        ref={fileRef}
                        data-testid="cv-input"
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="hidden"
                        onChange={(e) => pickFile(e.target.files?.[0])}
                      />
                    </div>
                  </div>

                  <Field label={f.cover}>
                    <Textarea data-testid="career-cover" name="cover_note" rows={4} value={form.cover_note} onChange={onChange} />
                  </Field>

                  <motion.button
                    whileTap={{ scale: 0.98 }}
                    data-testid="career-submit"
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#6A1E27] text-white py-4 hover:bg-[#4A151C] transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    {f.submit}
                  </motion.button>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm text-[#4A4A4A] mb-2 block">{label}</Label>
    {children}
  </div>
);

export default Careers;
